import { Suspense } from "react";
import * as THREE from "three";
import { useTexture } from "@react-three/drei";

/**
 * PBR material wiring for the throne hall room shell.
 *
 * - When `PBR_ENABLED` is false (default), every `<PBRSurface>` renders a
 *   plain `meshStandardMaterial` with the provided fallback color — this
 *   matches the existing primitive look and avoids broken texture loads
 *   while the textures/ folder is empty.
 * - When `PBR_ENABLED` is true, each surface loads albedo / normal /
 *   roughness / AO from `/textures/<name>/` using drei's `useTexture`,
 *   sets repeat-tiling on the U/V axes (configurable per call), and
 *   wires them into `meshStandardMaterial`.
 *
 * See `client/public/textures/README.md` for which Poly Haven CC0 packs
 * to download and how to lay them out before flipping `PBR_ENABLED`.
 */
export const PBR_ENABLED = false;

export interface PBRSurfaceMaterialProps {
  name: string;
  repeat?: [number, number];
  color?: string;
  roughness?: number;
  envMapIntensity?: number;
}

export function PBRSurfaceMaterial({
  name,
  repeat = [1, 1],
  color = "#888888",
  roughness = 0.9,
  envMapIntensity = 1.0,
}: PBRSurfaceMaterialProps): JSX.Element {
  if (!PBR_ENABLED) {
    return (
      <meshStandardMaterial
        color={color}
        roughness={roughness}
        envMapIntensity={envMapIntensity}
      />
    );
  }
  return (
    <Suspense
      fallback={
        <meshStandardMaterial color={color} roughness={roughness} />
      }
    >
      <PBRMaterialInner
        name={name}
        repeat={repeat}
        color={color}
        envMapIntensity={envMapIntensity}
      />
    </Suspense>
  );
}

interface PBRInnerProps {
  name: string;
  repeat: [number, number];
  color: string;
  envMapIntensity: number;
}

function PBRMaterialInner({
  name,
  repeat,
  color,
  envMapIntensity,
}: PBRInnerProps): JSX.Element {
  const base = `/textures/${name}`;
  const textures = useTexture([
    `${base}/albedo.jpg`,
    `${base}/normal.jpg`,
    `${base}/roughness.jpg`,
    `${base}/ao.jpg`,
  ]) as THREE.Texture[];
  const [albedo, normal, roughnessMap, ao] = textures as [
    THREE.Texture,
    THREE.Texture,
    THREE.Texture,
    THREE.Texture,
  ];

  for (const t of textures) {
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat[0], repeat[1]);
    t.anisotropy = 8;
  }
  albedo.colorSpace = THREE.SRGBColorSpace;

  return (
    <meshStandardMaterial
      map={albedo}
      normalMap={normal}
      roughnessMap={roughnessMap}
      aoMap={ao}
      color={color}
      envMapIntensity={envMapIntensity}
    />
  );
}
